import React, { useState } from 'react';
import { DeleteOutlined, InboxOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Image, Space, Upload, message } from 'antd';
import type { UploadFile, UploadProps } from 'antd';
import Modal from '../Modal';
import PrimaryButton from '../PrimaryButton';
import SecondaryButton from '../SecondaryButton';
import Dragger from 'antd/es/upload/Dragger';
import './_assetimagesmodal.css';
import { ApiHandler } from '../../services/apiHandler';
import ImageResizer from 'react-image-file-resizer';

interface ImageModalProps {
  show: boolean;
  close: () => void;
  alert: (type: 'error' | 'warning' | 'success', message: string) => void;
  trackingId?: string;
  afterUpload: (fileList: any) => void;
  fileList: UploadFile[];
  setFileList: React.Dispatch<React.SetStateAction<UploadFile[]>>;
}

type FileType = Parameters<any>[0];

const getBase64 = (file: any): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });

const AssetImagesModalNew = ({ show, close, alert, trackingId, afterUpload, fileList, setFileList }: ImageModalProps) => {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState('');

  const handleChange: UploadProps['onChange'] = async ({ fileList: newFileList }) => {
    const updatedList = await Promise.all(
      newFileList.map(async (file, index) => {
        if (!file.url && !file.preview && file.originFileObj) {
          file.preview = await getBase64(file.originFileObj as FileType);
        }
        return file;
      })
    );

    const draggerProxy = document.getElementsByClassName('dragger-custom-class')[0] as HTMLElement;
    if (draggerProxy && updatedList.length > 0) {
      draggerProxy.style.setProperty('transition', 'width 0.2s ease');
      draggerProxy.getBoundingClientRect();
      draggerProxy.style.setProperty('width', '50%', '!important');
    }

    setTimeout(() => {
      setFileList(updatedList);
    }, 1000);
  };

  const getLocationAndUpload = () => {
    navigator.geolocation.getCurrentPosition(
      (position: GeolocationPosition) => {
        handleUpload(position.coords.latitude, position.coords.longitude);
      },
      (e: any) => {
        console.log('Error fetching user location: ', e);
        handleUpload(0, 0);
      },
      { enableHighAccuracy: true, timeout: 3000 }
    );
  };

  // function base64ToBlob(base64: string) {
  //   const byteString = atob(base64.split(',')[1]);
  //   const mimeString = base64.split(',')[0].split(':')[1].split(';')[0];
  //   const ab = new ArrayBuffer(byteString.length);
  //   const ia = new Uint8Array(ab);
  //   for (let i = 0; i < byteString.length; i++) {
  //     ia[i] = byteString.charCodeAt(i);
  //   }
  //   return new Blob([ab], { type: mimeString });
  // }

  const handleUpload = async (latitude: number, longitude: number) => {
    var tempFileList: any = [...fileList];
    var index = 0;
    for (const file of fileList) {
      const resizedBase64Uri = await resizeImage(file.originFileObj); // this is your URI

      const payload = {
        file: resizedBase64Uri, // sending URI directly
        type: 'FACELIFT',
        tracking_id: trackingId || '',
        latitude: latitude.toString(),
        longitude: longitude.toString(),
        ext: file.name.split('.').pop(),
      };

      try {
        const response = await ApiHandler({
          apiName: 'upload',
          params: {},
          body: payload,
        });
        if (response && response.data && response.data.path) {
          tempFileList[index]['path'] = response.data.path;
          tempFileList[index]['uploaded'] = true;
          tempFileList[index]['latitude'] = latitude;
          tempFileList[index]['longitude'] = longitude;
          tempFileList[index]['type'] = 'FACELIFT';
        }
        alert('success', response.data.message);
        setFileList([]);
      } catch (error: any) {
        console.log('Error uploading image', error);
        alert('error', error.data.message);
      }
      index += 1;
    }
    setFileList(tempFileList);
    afterUpload(tempFileList);
  };

  const resizeImage = (
    file: any,
    width = 800,
    height = 800,
    format = 'JPEG',
    quality = 100,
    rotation = 0,
    outputType = 'base64'
  ): Promise<string> => {
    return new Promise((resolve, reject) => {
      ImageResizer.imageFileResizer(
        file,
        width,
        height,
        format,
        quality,
        rotation,
        (value: string | File | Blob | ProgressEvent<FileReader>) => {
          if (typeof value === 'string') {
            resolve(value); // base64 string (your case)
          } else if (value instanceof Blob || value instanceof File) {
            // convert to base64 if needed
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(value);
          } else if (value instanceof ProgressEvent) {
            resolve((value.target as any)?.result as string);
          } else {
            reject('Unsupported output type');
          }
        },
        outputType
      );
    });
  };

  const props: UploadProps = {
    onRemove: (file) => {
      const index = fileList.indexOf(file);
      const newFileList = fileList.slice();
      newFileList.splice(index, 1);
      setFileList(newFileList);
    },
    beforeUpload: (file: any) => {
      // prevent automatic upload
      const reader = new FileReader();
      reader.onload = (e) => {
        file.preview = e.target?.result;
        setFileList((prev) => [...prev, file]);
      };
      reader.readAsDataURL(file);
      return false; // stop upload
    },
    fileList,
    listType: 'picture-card',
    onChange: handleChange,
    multiple: true,
    showUploadList: false,
    accept: 'image/jpeg,image/png',
  };

  const handleRemove = (uid: string) => {
    const tempFileList = [...fileList];
    const draggerProxy = document.getElementsByClassName('dragger-custom-class')[0] as HTMLElement;
    // console.log('draggerProxy', draggerProxy, tempFileList.length);
    if (tempFileList.length === 1) {
      draggerProxy.style.setProperty('transition', 'width 0.5s ease');
      draggerProxy.style.setProperty('width', '100%', 'important');
    }
    setFileList((prev) => prev.filter((file) => file.uid !== uid));
  };

  return (
    <Modal show={show}>
      <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable image-upload-modal">
        <div className="modal-content app-card border-0 p-0">
          <div className="modal-header" style={{ marginBottom: '' }}>
            <h5 className="modal-title">{'IMAGES'}</h5>
            <button
              type="button"
              className="btn-close"
              onClick={(e: any) => {
                e.preventDefault();
                close();
              }}></button>
          </div>

          <div className="d-flex flex-row px-3 pb-3 justify-content-between w-100">
            <Dragger style={{ width: '100% !important' }} {...props} className="dragger-custom-class" disabled={fileList.length >= 10} maxCount={10}>
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">Click or drag file to this area to upload</p>
              <p className="ant-upload-hint">You can upload upto 10 images</p>
            </Dragger>
            {fileList.length > 0 && (
              <Image.PreviewGroup
                preview={{
                  onChange: (current, prev) => console.log(`current index: ${current}, prev index: ${prev}`),
                  zIndex: 2000,
                }}>
                <Space style={{ transition: 'width 0.5s ease' }} className="d-flex flex-row flex-wrap w-50 px-2 align-items-lg-start">
                  {fileList.map((file) => (
                    // <Image width={50} height={50} src={file.url || (file.preview as string)} />
                    <div key={file.uid} style={{ position: 'relative', display: 'inline-block' }}>
                      <Image width={50} height={50} src={file.preview} style={{ objectFit: 'cover', borderRadius: 8 }} />
                      <Button
                        type="primary"
                        danger
                        size="small"
                        shape="circle"
                        icon={<DeleteOutlined style={{ fontSize: '10px' }} />}
                        onClick={() => handleRemove(file.uid)}
                        style={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          zIndex: 10,
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          height: '20px',
                          minWidth: '20px',
                          width: '20px',
                        }}
                      />
                    </div>
                  ))}
                </Space>
              </Image.PreviewGroup>
            )}
          </div>
          {previewImage && (
            <Image
              wrapperStyle={{ display: 'none' }}
              preview={{
                visible: previewOpen,
                zIndex: 2000,
                onVisibleChange: (visible) => setPreviewOpen(visible),
                afterOpenChange: (visible) => !visible && setPreviewImage(''),
              }}
              src={previewImage}
            />
          )}

          <div className="row-wise vertical-center justify-content-end align-items-center mx-3 mb-3 mt-1 gap-2">
            <div className="d-flex flex-row gap-2">
              <SecondaryButton
                className="w-100"
                label={'Cancel'}
                clicked={() => {
                  setFileList([]);
                  close();
                }}
              />
              <PrimaryButton className="w-100 blue-bg" label="Save" clicked={getLocationAndUpload} disable={fileList.length === 0} />
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default AssetImagesModalNew;
